import React, { FC, useEffect, useState } from 'react'
import cx from 'classnames'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Responsive, WidthProvider } from '@goodrequest/react_grid_layout'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

// types
import { IElementItem, IFormItemData, IGridItem, ITabBoxLayoutRef } from '../../../types/interfaces'

// assets
import { ReactComponent as HeadingIcon } from '../../../assets/icons/ElementIcons/heading-icon.svg'
import { ReactComponent as CheckBoxIcon } from '../../../assets/icons/ElementIcons/checkbox-icon.svg'
import { ReactComponent as DropDownIcon } from '../../../assets/icons/ElementIcons/dropdown-icon.svg'
import { ReactComponent as InputIcon } from '../../../assets/icons/ElementIcons/input-icon.svg'
import { ReactComponent as RadioIcon } from '../../../assets/icons/ElementIcons/radio-icon.svg'
import { ReactComponent as TextareaIcon } from '../../../assets/icons/ElementIcons/textarea-icon.svg'
import { ReactComponent as ButtonIcon } from '../../../assets/icons/ElementIcons/button-icon.svg'
import { ReactComponent as TextIcon } from '../../../assets/icons/ElementIcons/text-icon.svg'
import { ReactComponent as TableIcon } from '../../../assets/icons/ElementIcons/table-icon.svg'
import { ReactComponent as HorizontalLineIcon } from '../../../assets/icons/ElementIcons/horizontal-line-icon.svg'
import { ReactComponent as DateIcon } from '../../../assets/icons/ElementIcons/date-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/icons/ElementIcons/calendar-icon.svg'
import { ReactComponent as TabIcon } from '../../../assets/icons/ElementIcons/section-icon.svg'
import { ReactComponent as ImageIcon } from '../../../assets/icons/ElementIcons/image-icon.svg'
import { ReactComponent as SelectFromTableIcon } from '../../../assets/icons/ElementIcons/webview-icon.svg'

// components
// eslint-disable-next-line import/no-cycle
import TabElement from './TabsElement'

// reducers
import { FORM_EDITED, LOAD } from '../../../reducers/form/formTypes'

// utils
import { ELEMENT_TYPES, LAYOUT_ROW_HEIGHT, DROPPING_ELEMENT, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT, PAGE } from '../../../utils/enums'
import { patchReq } from '../../../utils/request'
import { getApiEndpointPath } from '../../../utils/helper'
import { RootState } from '../../../reducers'

const ResponsiveReactGridLayout = WidthProvider(Responsive)

type Props = {
	formId?: string
	layouts: IGridItem[]
	items: IElementItem
	selectedItem: string
	openItem: Function
	onLayoutChange: any
	selectedNestedTabItem: string
	onNewItemAdded: Function
	itemKey: string
	itemY: number
	itemX: number
	layoutHeight: number
	reload: Function
	resizeElement: Function
	mainContainerWidth: number
	onClickSwitch: Function
	onAddNewTab: Function
	onRemoveTab: Function
	selectedTabItem: string
	openItemInTabsElement: Function
	onLayoutChangeInTabsElement: Function
	onNewItemAddedToTabsElement: Function
	tabBoxLayoutReferences: ITabBoxLayoutRef
	tabKey: string
	nestedElementsRendered: Function
	onDragStartInTab: Function
	isDroppable: boolean
	onRender?: Function
	page?: PAGE
}

const TabsElementsBuilder: FC<Props> = (props) => {
	const {
		layouts,
		onLayoutChange,
		selectedItem,
		items,
		openItem,
		itemKey,
		itemX,
		itemY,
		layoutHeight,
		reload,
		formId,
		resizeElement,
		page,
		mainContainerWidth,
		onRender,
		onClickSwitch,
		onAddNewTab,
		onRemoveTab,
		selectedTabItem,
		openItemInTabsElement,
		onLayoutChangeInTabsElement,
		onNewItemAddedToTabsElement,
		tabBoxLayoutReferences,
		tabKey,
		nestedElementsRendered,
		onDragStartInTab,
		isDroppable,
		selectedNestedTabItem
	} = props
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const [removePlaceholder, setRemovePlaceholder] = useState<boolean>(false)
	const placeholderHeight = useSelector((state: RootState) => state.formTemplate.placeholder.height)
	const placeholderWidth = useSelector((state: RootState) => state.formTemplate.placeholder.width)

	const renderIcon = (type: string) => {
		switch (type) {
			case ELEMENT_TYPES.TEXT:
				return <TextIcon width={20} height={20} />
			case ELEMENT_TYPES.INPUT:
				return <InputIcon width={20} height={20} />
			case ELEMENT_TYPES.DROPDOWN_BUTTON:
				return <DropDownIcon width={20} height={20} />
			case ELEMENT_TYPES.CHECK_BOX:
				return <CheckBoxIcon width={20} height={20} />
			case ELEMENT_TYPES.RADIO_BUTTON:
				return <RadioIcon width={20} height={20} />
			case ELEMENT_TYPES.TEXT_AREA:
				return <TextareaIcon width={20} height={20} />
			case ELEMENT_TYPES.HORIZONTAL_LINE:
				return <HorizontalLineIcon width={20} height={20} />
			case ELEMENT_TYPES.DATE_PICKER:
				return <DateIcon width={20} height={20} />
			case ELEMENT_TYPES.CALENDAR:
				return <CalendarIcon width={20} height={20} />
			case ELEMENT_TYPES.BUTTON:
				return <ButtonIcon width={20} height={20} />
			case ELEMENT_TYPES.TABLE:
				return <TableIcon width={20} height={20} />
			case ELEMENT_TYPES.TITLE:
				return <HeadingIcon width={20} height={20} />
			case ELEMENT_TYPES.TAB_BOX:
				return <TabIcon width={20} height={20} />
			case ELEMENT_TYPES.IMAGE:
				return <ImageIcon width={20} height={20} className={'icon--stroked'} />
			case ELEMENT_TYPES.SELECT_FROM_TABLE:
				return <SelectFromTableIcon width={20} height={20} />
			default:
				return undefined
		}
	}

	/*
		when user drop new item to tabs element layout this function
		send context data which type of element item is dropped to tabs element layout
	*/
	const onDropToTab = (layout: IGridItem[], layoutItem: IGridItem, event: any) => {
		try {
			props.onNewItemAdded(JSON.parse(event.dataTransfer.getData('dropItem')), layoutItem)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const renderLayoutElements = () =>
		layouts.map((gridItem: IGridItem) => {
			const editClass = cx({
				'opened-element': selectedItem === gridItem.i || selectedNestedTabItem === gridItem.i,
				'tab-box': items[`${gridItem.i}`]?.type === ELEMENT_TYPES.TAB_BOX
			})
			const formAttributes = items[`${gridItem.i}`]?.formItemGeneral

			const renderLabel = (label: string, type: string) => {
				if (type === ELEMENT_TYPES.HORIZONTAL_LINE) {
					return t('loc:Horizontal line')
				}
				if (type === ELEMENT_TYPES.IMAGE) {
					return t('loc:Image')
				}
				return label
			}

			// set remove placeholder and trigger removing in tab box or nested tab box builder layout
			const onDragStartInNestedTab = () => {
				setRemovePlaceholder(true)
			}

			return (
				<div onClick={() => openItem(gridItem)} id={`${gridItem.i}`} className={editClass} key={gridItem.i}>
					{items[`${gridItem.i}`]?.type === ELEMENT_TYPES.TAB_BOX && items[`${gridItem.i}`]?.tabs ? (
						<TabElement
							formId={formId}
							// needs to add value x and y position for nested tab box relative to parent tab box element layout
							itemX={itemX + gridItem.x}
							itemY={itemY + gridItem.y}
							tabs={items[`${gridItem.i}`]?.tabs}
							itemKey={gridItem.i}
							locked={gridItem.static || false}
							tabBoxParenKey={itemKey}
							tabBoxParentTabKey={tabKey}
							onClickSwitch={onClickSwitch}
							onAddNewTab={onAddNewTab}
							selectedNestedTabItem={selectedNestedTabItem}
							onRemoveTab={onRemoveTab}
							selectedItem={selectedTabItem}
							openItem={openItemInTabsElement}
							onLayoutChange={onLayoutChangeInTabsElement}
							onNewItemAdded={onNewItemAddedToTabsElement}
							layoutHeight={tabBoxLayoutReferences?.[`${gridItem.i}`]?.height}
							onRender={onRender}
							reload={reload}
							resizeElement={resizeElement}
							page={page}
							mainContainerWidth={mainContainerWidth}
							selectedTabItem={selectedTabItem}
							openItemInTabsElement={openItemInTabsElement}
							onLayoutChangeInTabsElement={onLayoutChangeInTabsElement}
							onNewItemAddedToTabsElement={onNewItemAddedToTabsElement}
							tabBoxLayoutReferences={tabBoxLayoutReferences}
							nestedElementsRendered={nestedElementsRendered}
							onDragStartInTab={() => {
								onDragStartInNestedTab()
								onDragStartInTab()
							}}
							isDroppable={isDroppable}
						/>
					) : (
						<div className='element-label-div'>
							<label
								style={{
									color: formAttributes?.labelColor
								}}
							>
								{renderLabel(formAttributes?.label, items[`${gridItem.i}`]?.type)}
							</label>
							<div className={'element-icons-box'}>
								<div className='element-icon-box'>{renderIcon(items[`${gridItem.i}`]?.type)}</div>
							</div>
						</div>
					)}
				</div>
			)
		})

	// update item position asn size in database
	const updateItem = async (item: IGridItem) => {
		// update formItemGeneral row and height
		const formItemData = { ...items[`${item.i}`]?.formItemGeneral }
		formItemData.row = item.h * LAYOUT_ROW_HEIGHT
		formItemData.col = item.w
		const formItem: IFormItemData = {
			x: item.x,
			y: item.y,
			i: item.i,
			h: item.h,
			w: item.w,
			formItemGeneral: formItemData
		}
		try {
			await patchReq(getApiEndpointPath(page, formId, items[`${item.i}`].id), undefined, formItem)
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
			dispatch({ type: LOAD, load: true })
		} catch (err: any) {
			// if some error occurred when user updating position of element, reload whole form items
			reload()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	useEffect(() => {
		// callback to parent component (FormBuilderPage) when this component is rendered
		nestedElementsRendered()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<ResponsiveReactGridLayout
			className='layout responsive-tabs-element-layout'
			rowHeight={8}
			cols={{
				lg: 12,
				md: 12,
				sm: 12,
				xs: 12,
				xxs: 12
			}}
			margin={[10, 0]}
			layouts={{ lg: layouts }}
			droppingItem={{
				i: DROPPING_ELEMENT.KEY,
				h: placeholderHeight || DROPPING_ELEMENT.HEIGHT,
				w: placeholderWidth || DROPPING_ELEMENT.WIDTH
			}}
			onLayoutChange={onLayoutChange}
			onDrop={(layout: any, layoutItem: any, _event: any) => onDropToTab(layout, layoutItem, _event)}
			measureBeforeMount={true}
			useCSSTransforms={true}
			compactType={null}
			preventCollision={true}
			isDroppable={isDroppable}
			style={{ minHeight: layoutHeight, maxHeight: layoutHeight }}
			layoutKey={itemKey}
			itemX={itemX}
			itemY={itemY}
			removePlaceholder={removePlaceholder}
			// callback from lib when placeholder is removed
			placeholderRemoved={() => {
				setRemovePlaceholder(false)
			}}
			onDragStart={debounce(() => {
				onDragStartInTab()
			}, 10)}
			mainContainerWidth={mainContainerWidth}
			onResizeStop={debounce((layout: IGridItem[], previousItem: IGridItem, item: IGridItem) => {
				resizeElement(layout, item, itemKey, tabKey)
				updateItem(item)
			}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)}
			onDragStop={debounce((layout: IGridItem[], previousItem: IGridItem, item: IGridItem) => {
				updateItem(item)
			}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)}
		>
			{renderLayoutElements()}
		</ResponsiveReactGridLayout>
	)
}

export default React.memo(TabsElementsBuilder)
